import * as Api from '@ViewModels';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { AIConversation } from '../../extViewmodels/sdk/models/AI';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

const BASE_QUERY_KEY = `aiconversation/filter`;

type QueryParams = {
	impersonationContext?: Api.IImpersonationContext;
	filter?: Api.AIConversationFilter;
	enabled?: boolean;
	staleTime?: number;
	refetchInterval?: number | false;
	refetchOnWindowFocus?: boolean;
};

export function getAIConversationFilterQueryKey(queryParams: QueryParams): any[] {
	const impersonationKey = queryParams.impersonationContext
		? {
				accountId: queryParams.impersonationContext.account?.id,
				userId: queryParams.impersonationContext.user?.id,
			}
		: undefined;

	return [BASE_QUERY_KEY, queryParams.filter, impersonationKey];
}

export function useAIConversationFilterQuery(queryParams: QueryParams) {
	const userSession = useUserSession();
	return useQuery({
		queryKey: getAIConversationFilterQueryKey(queryParams),
		queryFn: () =>
			userSession.webServiceHelper.callAsync<AIConversation[]>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `aiconversation/filter`,
					impersonationContext: queryParams.impersonationContext,
				}),
				'POST',
				queryParams.filter
			),
		enabled: queryParams.enabled ?? true,
		staleTime: queryParams.staleTime,
		refetchInterval: queryParams.refetchInterval,
		refetchOnWindowFocus: queryParams.refetchOnWindowFocus,
	});
}

export function useAIConversationFilterSuspenseQuery(queryParams: QueryParams) {
	const userSession = useUserSession();
	return useSuspenseQuery({
		queryKey: getAIConversationFilterQueryKey(queryParams),
		queryFn: () =>
			userSession.webServiceHelper.callAsync<AIConversation[]>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `aiconversation/filter`,
					impersonationContext: queryParams.impersonationContext,
				}),
				'POST',
				queryParams.filter
			),
		staleTime: queryParams.staleTime,
		refetchInterval: queryParams.refetchInterval,
		refetchOnWindowFocus: queryParams.refetchOnWindowFocus,
	});
}

export const invalidateAllAIConversationFilterQueries = () => {
	reactQueryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
};

// Prepend a newly-created conversation into the cached filter result so consumers
// reading `data[0]` see the new conversation immediately, without waiting for a
// refetch. Used after creating a fresh conversation from an error boundary where
// the remounted children would otherwise re-read stale (broken) cached data.
export const prependAIConversationToFilterCache = (queryParams: QueryParams, conversation: AIConversation) => {
	reactQueryClient.setQueryData<AIConversation[]>(getAIConversationFilterQueryKey(queryParams), prev =>
		prev ? [conversation, ...prev] : [conversation]
	);
};

export const updateAIConversationInFilterCache = (queryParams: QueryParams, conversation: AIConversation) => {
	reactQueryClient.setQueryData<AIConversation[]>(getAIConversationFilterQueryKey(queryParams), prev =>
		prev ? prev.map(x => (x.id === conversation.id ? conversation : x)) : prev
	);
};
